import {isFunction} from 'lodash';
import {Fragment, cloneElement, useCallback, useMemo, useState} from 'react';
import {Position, Tooltip as ReactTippy, Trigger} from 'react-tippy';

import {Icon, TestAttribute, WithTestAttributeProps} from 'components';

import {DropdownChildren} from './DropdownChildren';

import 'react-tippy/dist/tippy.css';

type TriggerNodeProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
}

type Props = {
    children: React.ReactElement | React.ReactElement[] | ((props: {onClose: () => void}) => React.ReactElement);
    triggerNode: React.ReactElement | ((props: TriggerNodeProps) => React.ReactElement);
    position?: Position;
    trigger?: Trigger;
    open?: boolean;
    disabled?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    childrenClassName?: string;
    className?: string;
    style?: {[key: string]: string};
    controlled?: boolean;
    distance?: number;
    offset?: number;
    hideOnClick?: boolean;
    transparent?: boolean;
    icon?: boolean;
} & WithTestAttributeProps;

export type DropdownProps = Props;

export const Dropdown = ({
    triggerNode,
    children,
    open: openProp,
    disabled,
    onOpen,
    onClose,
    position = 'bottom-end',
    trigger = 'click',
    controlled = true,
    distance = 0,
    offset = 0,
    childrenClassName,
    className,
    hideOnClick = true,
    style = {display: 'inline-flex'},
    transparent,
    icon,
    testAttribute
}: Props) => {
    const [localOpen, setLocalOpen] = useState(false);
    const open = useMemo(() => !disabled && (openProp ?? localOpen), [disabled, openProp, localOpen]);
    const handleShow = useCallback(() => {
        setLocalOpen(true);
        onOpen?.();
    }, [setLocalOpen, onOpen]);

    const handleHide = useCallback(() => {
        setLocalOpen(false);
        onClose?.();
    }, [setLocalOpen, onClose]);

    const triggerNodeProps: TriggerNodeProps = useMemo(() =>
        ({open, setOpen: handleShow}),
    [open, handleShow]);

    const renderTriggerNode = useCallback(() => {
        if (isFunction(triggerNode)) {
            return triggerNode(triggerNodeProps);
        }
        return cloneElement(triggerNode);
    }, [triggerNode, triggerNodeProps]);

    const renderWrappedTriggerNode = useCallback(() =>
        icon ?
            <div className='tw-flex tw-items-center tw-text-dark'>
                {renderTriggerNode()}
                <Icon name='chevron-down' size='xs' className='tw-ml-1 self-center' />
            </div>
            :
            renderTriggerNode(),
    [icon, renderTriggerNode]);

    return (
        <Fragment>
            <ReactTippy
                position={position}
                trigger={trigger}
                interactive={controlled}
                arrow={false}
                hideOnClick={hideOnClick}
                open={open}
                distance={distance}
                offset={offset}
                html={
                    <DropdownChildren
                        className={childrenClassName}
                        transparent={transparent}
                        hideOnClick={hideOnClick}
                        onClose={handleHide}
                    >
                        {children}
                    </DropdownChildren>
                }
                className={className}
                style={style}
                zIndex={null}
                theme='transparent'
                onShow={handleShow}
                onHide={handleHide}
            >
                <TestAttribute testAttribute={testAttribute}>{renderWrappedTriggerNode()}</TestAttribute>
            </ReactTippy>
            {open && <div className='tw-inset-0 tw-fixed tw-opacity tw-z-main-menu' onClick={handleHide} />}
        </Fragment>
    );
};